import type { PgmAuditFrame, PgmAuditListItem, PgmCueKind, PgmFixturePool } from '../services/api';

export const PGM_CUE_KINDS: PgmCueKind[] = ['curtain', 'banner', 'lateral_banner'];

export const CUE_CHIP_LABEL: Record<PgmCueKind, string> = {
  curtain: 'Curtain',
  banner: 'Banner',
  lateral_banner: 'Lateral',
};

export const FRAME_THUMB_HEIGHT = 100;

/** Min card width in the virtualized audit gallery grid. */
export const FRAME_CARD_MIN_WIDTH = 160;

/** Gap between cards in the audit gallery grid (px). */
export const FRAME_CARD_GAP = 8;

export function pgmAuditFrameRowHeight(cueCount: number): number {
  return FRAME_THUMB_HEIGHT + 34 + Math.max(1, cueCount) * 28 + 16;
}

export type SavedSelection = {
  tsec: number;
  pool: PgmFixturePool;
  cue: PgmCueKind;
  committedCaseId?: string;
  sourceKey: string;
  pgmPass?: boolean;
  pgmRejectReason?: string;
  mentionId?: string;
  auditSource?: 'block_frames' | 'pgm_audit';
};

export function auditFrameSrc(imageUrl: string): string {
  if (!imageUrl) return '';
  const pathOnly = imageUrl.startsWith('http://') || imageUrl.startsWith('https://')
    ? new URL(imageUrl).pathname
    : imageUrl.startsWith('/')
      ? imageUrl
      : `/${imageUrl}`;
  const token = localStorage.getItem('authToken');
  if (!token) return pathOnly;
  const sep = pathOnly.includes('?') ? '&' : '?';
  return `${pathOnly}${sep}access_token=${encodeURIComponent(token)}`;
}

export function framePassesForCue(
  frame: PgmAuditFrame,
  cue: PgmCueKind,
  runCue: PgmCueKind | 'all',
): boolean {
  if (frame.cues?.[cue]) return frame.cues[cue]!.pass;
  if (runCue === cue) return frame.pass;
  return false;
}

export function frameRejectReasonForCue(
  frame: PgmAuditFrame,
  cue: PgmCueKind,
  runCue: PgmCueKind | 'all',
): string | undefined {
  if (frame.cues?.[cue]?.rejectReason) return frame.cues[cue]!.rejectReason;
  if (runCue === cue) return frame.rejectReason;
  return undefined;
}

export function cuesAvailableForRun(runCue: PgmCueKind | 'all'): PgmCueKind[] {
  if (runCue === 'all') return PGM_CUE_KINDS;
  return [runCue];
}

export function buildPgmSourceKey(contentId: string, tsec: number): string {
  const t = Math.max(0, Math.floor(tsec));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const stamp = `${String(h).padStart(2, '0')}-${String(m).padStart(2, '0')}-${String(s).padStart(2, '0')}`;
  return `${contentId}:${stamp}`;
}

export function normalizeTsec(tsec: number): number {
  return Math.max(0, Math.floor(tsec));
}

export const INCORRECT_EXAMPLE_REASON = 'curator:incorrect';

export function isIncorrectExampleSelection(s: SavedSelection): boolean {
  return s.pgmRejectReason === INCORRECT_EXAMPLE_REASON;
}

/** @deprecated use isIncorrectExampleSelection */
export function isFalsePositiveSelection(s: SavedSelection): boolean {
  return isIncorrectExampleSelection(s);
}

export function incorrectExampleTargetPool(pgmPass: boolean): PgmFixturePool {
  return pgmPass ? 'negative' : 'positive';
}

/** Gallery filter for the selected example cue's PGM verdict. */
export type PgmCueVerdictFilter = 'all' | 'pass' | 'fail';

export const PGM_CUE_VERDICT_FILTER_LABEL: Record<PgmCueVerdictFilter, string> = {
  all: 'All frames',
  pass: 'Cue pass',
  fail: 'Cue fail',
};

export function frameMatchesCueVerdictFilter(
  frame: PgmAuditFrame,
  cue: PgmCueKind,
  runCue: PgmCueKind | 'all',
  filter: PgmCueVerdictFilter,
): boolean {
  if (filter === 'all') return true;
  const passes = framePassesForCue(frame, cue, runCue);
  return filter === 'pass' ? passes : !passes;
}

/** Purple accent for selected PGM audit frame cards (matches chart / organic mention purple). */
export const PGM_FRAME_SELECTION_COLOR = '#9C27B0';

export function passRateForCue(run: PgmAuditListItem, cue: PgmCueKind): number | null {
  if (run.frameCount <= 0) return null;
  if (run.cue === 'all') {
    const count = run.passCountByCue?.[cue];
    if (count == null) return null;
    return count / run.frameCount;
  }
  if (run.cue === cue) return run.passRate;
  return null;
}

export function formatPassRate(rate: number | null): string {
  if (rate == null) return '—';
  return `${Math.round(rate * 100)}%`;
}
